import { Body, Circle } from "p2";

import { collisionGroups } from "../../config/CollisionGroups";
import { Colors } from "../../config/DesignConstants";
import Entity from "../../core/entity/Entity";
import AimSpring from "../../core/physics/AimSpring";
import { PolygonShape } from "../../core/util/PolygonUtils";
import { V, V2d } from "../../core/Vector";
import { DustExplosionEffect } from "../DustExplosionEffect";
import BrokenExpandEffect from "../effects/BrokenExpandEffect";
import { applyColor, VectorFilter } from "../graphics";
import VectorSprite from "../graphics/VectorSprite";
import { createPolygonSegments } from "../graphics/VectorSpriteUtils";
import { Hittable } from "../Hittable";
import { ShieldDamager } from "../interfaces/ShieldDamager";
import { LevelEntity } from "../LevelEntity";

const POLYGON_STROKE_WIDTH = 1;
const DEFAULT_EXPLOSION_LIFETIME = 1.4;
const DEFAULT_EXPLOSION_VELOCITY_SCALE = 50.0;

const HIT_FLASH_DURATION = 0.06;
const HIT_FLASH_COLOR = Colors.WHITE;

export interface EnemyConfig {
  readonly health: number;
  readonly killPoints: number;
  readonly mass: number;
  readonly visualConfig: PolygonShape;
  readonly movement?: {
    readonly maxSpeed?: number;
    readonly acceleration?: number;
    readonly damping?: number;
  };
}

export abstract class BaseEnemy
  extends LevelEntity
  implements Entity, Hittable, ShieldDamager
{
  body!: NonNullable<LevelEntity["body"]>;

  currentHealth!: number;

  config: EnemyConfig;
  vectorSprite: VectorSprite;
  aimSpring: AimSpring;

  // Movement parameters with defaults
  protected maxSpeed: number;
  protected acceleration: number;
  protected damping: number;

  private initialPosition: V2d;

  constructor(position: V2d, config: EnemyConfig) {
    super();
    this.tags = ["enemy", "levelEntity"];
    this.initialPosition = position;
    this.config = config;

    // Initialize health
    this.currentHealth = this.config.health;

    // Initialize movement parameters with defaults
    this.maxSpeed = config.movement?.maxSpeed ?? 100;
    this.acceleration = config.movement?.acceleration ?? 80;
    this.damping = config.movement?.damping ?? -0.5;

    // Create line segment renderer for visual
    this.vectorSprite = this.addChild(
      new VectorSprite(
        createPolygonSegments(
          config.visualConfig.points,
          config.visualConfig.color,
          config.visualConfig.strokeWidth || POLYGON_STROKE_WIDTH,
        ),
        "main",
      ),
    );

    // Create the physics body now that visual config is available
    this.body = this.createBody(this.initialPosition);

    // Create aim spring for rotation control
    this.aimSpring = new AimSpring(this.body, {
      stiffness: 10,
      damping: 1,
    });
    this.springs = [this.aimSpring];
  }

  protected createBody(position: V2d): Body {
    const body = new Body({
      mass: this.config.mass,
      collisionResponse: false,
      position: position.clone(),
    });

    // Create physics body from polygon
    const success = body.fromPolygon(this.config.visualConfig.points, {
      optimalDecomp: false,
      removeCollinearPoints: false,
    });

    if (!success) {
      console.warn(
        `Failed to create physics body from polygon for ${this.constructor.name}`,
      );
      // Fallback to a simple circle if polygon creation fails
      body.addShape(
        new Circle({
          radius: 10,
        }),
      );
    }

    // Set collision properties
    for (const shape of body.shapes) {
      shape.collisionGroup = collisionGroups("Enemy");
      shape.collisionMask = collisionGroups("Bullet", "Explosion", "Turret");
    }

    return body;
  }

  onRender(dt: number) {
    if (this.body && this.vectorSprite) {
      // Update renderer position and rotation from physics body
      this.vectorSprite.setPosition(this.body.position);
      this.vectorSprite.setRotation(this.body.angle);
    }
  }

  takeDamage(damage: number) {
    if (this.isDestroyed()) return;

    this.currentHealth -= damage;

    if (this.currentHealth <= 0) {
      this.die();
    } else {
      // Start flash effect with a new flash filter
      this.vectorSprite.addFilter(
        new VectorFilter(applyColor(HIT_FLASH_COLOR), HIT_FLASH_DURATION),
      );

      // Dispatch impact event for non-fatal hit
      this.dispatch("bulletImpact", {
        targetType: "enemy" as const,
        position: this.getPosition(),
        damage: damage,
        remainingHealth: this.currentHealth,
      });
    }
  }

  takeHit(damage: number) {
    this.takeDamage(damage);
  }

  protected die() {
    if (this.isDestroyed()) return;

    // Create explosion effect
    this.createExplosion();

    // Award points
    this.dispatch("scoreAdded", { points: this.config.killPoints });

    // Dispatch enemy destroyed event for sound effects
    this.dispatch("enemyDestroyed", {
      enemyType: this.constructor.name.replace("Enemy", "").toLowerCase(),
      points: this.config.killPoints,
    });

    // Allow subclasses to add custom death behavior
    this.onDeath();

    this.destroy();
  }

  protected onDeath() {
    // Override in subclasses for custom death behavior
  }

  protected createExplosion() {
    const visualConfig = this.config.visualConfig;
    const position = this.getPosition();

    // Get current velocity for inheritance
    const currentVelocity = this.body ? V(this.body.velocity) : undefined;

    // Get current rotation from renderer
    const currentRotation = this.vectorSprite?.sprite?.rotation || 0;

    // Create broken expand effect (debris)
    this.getCurrentLevel()?.addChild(
      new BrokenExpandEffect({
        position,
        polygon: visualConfig.points,
        rotation: currentRotation,
        colors: [
          visualConfig.color,
          visualConfig.secondaryColor || visualConfig.color,
        ],
        lifetime: DEFAULT_EXPLOSION_LIFETIME,
        velocityScale: DEFAULT_EXPLOSION_VELOCITY_SCALE,
        inheritedVelocity: currentVelocity,
      }),
    );

    // Create dust explosion effect
    this.getCurrentLevel()?.addChild(
      new DustExplosionEffect({
        position,
        color: visualConfig.color,
        polygonVertices: visualConfig.points,
      }),
    );
  }

  getPosition(): V2d {
    return this.body ? V(this.body.position) : V(0, 0);
  }

  // ShieldDamager interface implementation
  getShieldDamage(): number {
    return 1; // Standard damage to shield when enemy collides
  }

  onShieldHit(): void {
    this.die(); // Enemies die when they hit the shield
  }

  //===========================================================================
  // AIM CONVENIENCE METHODS - Common rotation patterns for enemies
  //===========================================================================

  /**
   * Aim at a specific angle
   * @param angle Target angle in radians
   */
  aim(angle: number): void {
    this.aimSpring.restAngle = angle;
  }

  /**
   * Aim "down" toward the center/earth
   * Useful for bombers and enemies that need to face the planet
   */
  aimDown(): void {
    const position = V(this.body.position);
    const toCenter = V(0, 0).sub(position);
    const angleToCenter = Math.atan2(toCenter.y, toCenter.x);
    // Add PI/2 to make "down" face the center
    this.aimSpring.restAngle = angleToCenter + Math.PI / 2;
  }

  /**
   * Aim in the direction of movement
   * Most common for enemies that should face where they're going
   */
  aimWithMovement(): void {
    const velocity = V(this.body.velocity);
    if (velocity.magnitude > 0.5) {
      this.aimSpring.restAngle = Math.atan2(velocity.y, velocity.x);
    }
  }

  //===========================================================================
  // MOVEMENT CONVENIENCE METHODS - Common movement patterns for enemies
  //===========================================================================

  /**
   * Seek a target position with steering behavior
   * @param target Target position to move toward
   * @param arrivalRadius Optional radius to start slowing down (smooth arrival)
   */
  seekPosition(target: V2d, arrivalRadius?: number): void {
    const position = V(this.body.position);
    const velocity = V(this.body.velocity);

    // Calculate desired velocity
    const toTarget = target.sub(position);
    const distance = toTarget.magnitude;

    if (distance < 0.01) return; // Already at target

    let desiredSpeed = this.maxSpeed;

    // Slow down when approaching target (arrival behavior)
    if (arrivalRadius && distance < arrivalRadius) {
      desiredSpeed = this.maxSpeed * (distance / arrivalRadius);
    }

    const desiredVelocity = toTarget.normalize().mul(desiredSpeed);

    // Calculate steering force
    const steeringForce = desiredVelocity.sub(velocity);
    const forceMagnitude = Math.min(steeringForce.magnitude, this.acceleration);
    const force = steeringForce
      .normalize()
      .mul(forceMagnitude * this.body.mass);

    this.body.applyForce([force.x, force.y]);
  }

  /**
   * Move in a specific direction
   * @param direction Direction vector (will be normalized)
   * @param speed Optional speed multiplier (0-1, default 1)
   */
  moveInDirection(direction: V2d, speed = 1): void {
    const normalizedDir = direction.normalize();
    const force = normalizedDir.mul(this.acceleration * this.body.mass * speed);
    this.body.applyForce([force.x, force.y]);
  }

  /**
   * Apply braking force to slow down
   * @param strength Brake strength multiplier (default 1)
   */
  brake(strength = 1): void {
    const velocity = V(this.body.velocity);
    if (velocity.magnitude < 0.1) {
      this.body.velocity = [0, 0];
      return;
    }

    const brakeForce = velocity
      .normalize()
      .mul(-this.acceleration * this.body.mass * strength);
    this.body.applyForce([brakeForce.x, brakeForce.y]);
  }

  /**
   * Maintain a specific speed (accelerate or decelerate as needed)
   * @param targetSpeed Target speed to maintain (default: maxSpeed)
   */
  maintainSpeed(targetSpeed?: number): void {
    const velocity = V(this.body.velocity);
    const currentSpeed = velocity.magnitude;
    const desiredSpeed = targetSpeed ?? this.maxSpeed;

    if (currentSpeed < 0.1) return; // Not moving

    const speedDiff = desiredSpeed - currentSpeed;
    if (Math.abs(speedDiff) < 1) return; // Close enough

    const direction = velocity.normalize();
    const force = direction.mul(
      speedDiff * this.acceleration * 0.1 * this.body.mass,
    );
    this.body.applyForce([force.x, force.y]);
  }

  /**
   * Apply velocity damping based on configured damping factor
   */
  applyDamping(): void {
    const velocity = V(this.body.velocity);
    const dampingForce = velocity.mul(this.damping);
    this.body.applyForce([dampingForce.x, dampingForce.y]);
  }
}
